
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Plus, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChippieConversation } from '@/types/database.types';

interface ConversationSidebarProps {
  conversations: ChippieConversation[];
  currentConversation: ChippieConversation | null;
  onSelectConversation: (conversation: ChippieConversation) => void;
  onNewConversation: () => void;
  isOpen: boolean;
  onToggle: () => void;
  isLoading?: boolean;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  currentConversation,
  onSelectConversation,
  onNewConversation,
  isOpen,
  onToggle,
  isLoading = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className={cn(
      'flex-shrink-0 transition-all duration-300 ease-in-out border-r bg-background h-full',
      isOpen ? 'w-80' : 'w-12'
    )}>
      {/* Toggle Button */}
      <div className="p-2 border-b">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="w-full justify-center"
        >
          {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </div>

      {isOpen && (
        <>
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="h-5 w-5" />
              <h2 className="font-semibold">Conversations</h2>
            </div>
            <Button onClick={onNewConversation} className="w-full gap-2" disabled={isLoading}>
              <Plus className="h-4 w-4" />
              New Chat
            </Button>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-1 p-2">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs">Start a new chat to begin</p>
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <Card
                      key={conversation.id}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-muted/50 border-0 shadow-none',
                        currentConversation?.id === conversation.id && 
                        'bg-muted border-l-4 border-l-blue-500'
                      )}
                      onClick={() => onSelectConversation(conversation)}
                    >
                      <CardContent className="p-3">
                        <div className="space-y-1">
                          <h4 className="text-sm font-medium line-clamp-2">
                            {conversation.title}
                          </h4>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {formatDate(conversation.last_message_at)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  );
};

export default ConversationSidebar;
